'use client'

import type { ProductSimpleListProps } from '@maestro/commerce-blocks/components/product-simple-list/product-simple-list.types'
import type { SearchBarProps } from '@maestro/commerce-blocks/components/search-bar/search.types'

import classNames from 'classnames'
import { useTranslations } from 'next-intl'
import { memo, useMemo } from 'react'

import { ProductSimpleList } from '@maestro/commerce-blocks/components/product-simple-list'
import {
  SearchBarDirectHitProductAction,
  SearchLastSearchesList,
  SearchShortcuts,
  SearchSuggestionsList,
  SearchSuggestionsResults,
  SearchTopFoundProducts,
} from '@maestro/commerce-blocks/components/search-bar'
import { useSearchBar } from '@maestro/commerce-blocks/components/search-bar/batons/use-search-bar'
import { theme } from '@maestro/local/theme'
import { Input } from '@pearlconvert/ui/components/input'

import '@maestro/commerce-blocks/components/search-bar/styles/search-bar.scss'

const SearchBar = memo(function SearchBar(props: SearchBarProps) {
  const { searchShortcuts } = props
  const t = useTranslations('commerce-blocks')
  const {
    search: {
      searchBar: { isEnabledSearchBySku },
    },
  } = theme()
  const {
    handleBlurInput,
    handleInputChange,
    handleFocusInput,
    handleKeyDownInput,
    handleSubmitSearch,
    handleSuggestionSelectAction,
    hasNoSearchResults,
    hideSearch,
    activeOptionId,
    input,
    inputRef,
    isDesktopView,
    isInputTooShort,
    isLoadingPopularSearches,
    isMobileView,
    isSearchVisible,
    isSuggestionsPanelOpen,
    lastSearches,
    popularSearches,
    productsCount,
    removalMessage,
    removeSearch,
    resetSearch,
    searchResult,
    searchPanelId,
    setRemovalMessage,
    suggestions,
    wrapperRef,
  } = useSearchBar()

  const directSKUresults: ProductSimpleListProps['products'] = useMemo(() => {
    if (!isEnabledSearchBySku) {
      return []
    }
    return (
      searchResult?.hits?.filter?.(
        ({ sku, skus }) => skus?.includes(input) ?? sku === input
      ) ?? []
    )
  }, [input, isEnabledSearchBySku, searchResult?.hits])

  const searchSkuResultsEventListProps = useMemo(() => {
    return {
      listId: `search-sku-results`,
      listName: 'Search SKU Results',
      listType: 'search sku results',
    }
  }, [])

  const suggestionsCount = isInputTooShort
    ? lastSearches.length +
      popularSearches.length +
      (searchShortcuts?.length || 0)
    : suggestions.length

  const suggestionsStatusMessage =
    isSearchVisible && input !== ''
      ? t('search.suggestions-available', { count: suggestionsCount })
      : ''

  const searchBarClearButton = useMemo(() => {
    return (
      <button
        type="button"
        className="searchbar__clear"
        aria-label={t('search.clear')}
        onClick={() => resetSearch(true)}
      />
    )
  }, [resetSearch, t])

  return (
    <search
      className={classNames('searchbar__root', {
        'searchbar__root--active':
          (isSearchVisible && !hasNoSearchResults) ||
          (!!directSKUresults?.length && input !== ''),
        'searchbar__root--has-value': input !== '',
      })}
      ref={wrapperRef}
    >
      <span className="sr-only" aria-live="polite" aria-atomic="true">
        {suggestionsStatusMessage}
      </span>
      <form
        className="searchbar__form"
        action="/search/"
        method="get"
        onSubmit={handleSubmitSearch}
      >
        <div className="searchbar__form-wrapper">
          <Input
            id="searchBar"
            name="text"
            autoComplete="off"
            type="search"
            value={input}
            icon="search"
            placeholder={t('search.placeholder')}
            className="searchbar__field"
            role="combobox"
            aria-autocomplete="list"
            aria-controls={searchPanelId}
            aria-expanded={isSuggestionsPanelOpen}
            aria-haspopup="listbox"
            aria-activedescendant={activeOptionId}
            onBlur={handleBlurInput}
            onChange={(event) => {
              handleInputChange(event)
            }}
            onFocus={handleFocusInput}
            onKeyDown={handleKeyDownInput}
            ref={inputRef}
          />
          {input !== '' && (
            <div className="mobile-only">{searchBarClearButton}</div>
          )}
        </div>
        {(isSearchVisible || input !== '') && (
          <button
            type="button"
            className="searchbar__close"
            aria-label={t('search.cancel')}
            onClick={() => resetSearch(false)}
          >
            {t('_globals.cancel')}
          </button>
        )}
      </form>
      {isSuggestionsPanelOpen && (
        <div
          className="searchbar__suggestions"
          id={searchPanelId}
          role="listbox"
          onMouseDown={(e) => e.preventDefault()}
        >
          <div className="searchbar__suggestions-wrapper">
            {!!suggestions.length && !isInputTooShort && (
              <SearchSuggestionsResults
                suggestions={suggestions}
                removalMessage={removalMessage}
                handleSuggestionSelectAction={handleSuggestionSelectAction}
                activeOptionId={activeOptionId}
              />
            )}
            {isMobileView() && !isInputTooShort && searchResult && (
              <div className="searchbar__suggestions-node">
                <SearchTopFoundProducts
                  handleHideSearch={hideSearch}
                  searchResult={searchResult}
                  productsCount={productsCount}
                />
              </div>
            )}

            {isInputTooShort && !!lastSearches.length && (
              <SearchLastSearchesList
                lastSearches={lastSearches}
                hideSearch={hideSearch}
                removeSearch={removeSearch}
                setRemovalMessage={setRemovalMessage}
                activeOptionId={activeOptionId}
              />
            )}
            {isInputTooShort &&
              !!popularSearches.length &&
              (isLoadingPopularSearches ? (
                <div className="searchbar__loading">...loading</div>
              ) : (
                <SearchSuggestionsList
                  title={t('search.popular-searches')}
                  items={popularSearches}
                  onClick={handleSuggestionSelectAction}
                  activeOptionId={activeOptionId}
                />
              ))}
            {isInputTooShort && searchShortcuts && (
              <SearchShortcuts
                searchShortcuts={searchShortcuts}
                hideSearch={hideSearch}
                activeOptionId={activeOptionId}
              />
            )}
          </div>
        </div>
      )}
      {!!directSKUresults?.length && input !== '' && (
        <div className="searchbar__direct-sku-results">
          <div className="searchbar__direct-sku-results-wrapper">
            <ProductSimpleList
              products={directSKUresults}
              key={`search-sku-results`}
              productsTitleTag="h5"
              displayStockStatus={true}
              enableClientSideStockFetching={true}
              displayPrice={true}
              enableClientSidePriceFetching={true}
              addToCartType="none"
              renderProductAction={(product, isStockLoading) => (
                <SearchBarDirectHitProductAction
                  product={product}
                  eventListProps={searchSkuResultsEventListProps}
                  isStockLoading={isStockLoading}
                />
              )}
              eventListProps={searchSkuResultsEventListProps}
            />
          </div>
        </div>
      )}
      {/* Button moved here to change tab order
      as manual tabindex adjustment did not work. */}
      {isDesktopView() && input !== '' && searchBarClearButton}
    </search>
  )
})

export default SearchBar
